import * as i18nextModule from "i18next";
import type { i18n } from "i18next";
import { initReactI18next } from "react-i18next";
import { z } from "zod";
import { AppConfig } from "../config/app.config";
import { installConfigPostProcessor } from "mfe-lib-bfo-shared";

const i18next = (i18nextModule.default ?? i18nextModule) as i18n;

let initialized = false;

/**
 * Builds the language fallback hierarchy used by i18next.
 *
 * i18next follows BCP-47 language tag rules by default. BCP-47 (IETF
 * Best Current Practice 47) defines the standard format for language
 * identifiers, where subtags have specific meanings:
 *
 *   language[-script][-region][-variant]
 *
 * Example:
 *
 *   en-US
 *   - language: English
 *   - region: United States
 *
 * Custom application locales such as:
 *
 *   en-premier-islamic
 *
 * do not follow the standard BCP-47 structure because "premier" and
 * "islamic" are application-specific variants. Some locale parsers may
 * normalize this value (for example, interpreting it as script/region)
 * and change the ordering, causing i18next to resolve it incorrectly.
 *
 * This function treats application locales as custom identifiers and
 * builds the fallback chain based only on the available translation
 * resources:
 *
 *   en-premier-islamic -> en-premier -> en
 *   en-premier         -> en
 *   ms-premier         -> ms
 *
 * The generated fallback map is used by i18next's fallbackLng option,
 * and the resolver returned from this function overrides i18next's
 * default hierarchy resolution so custom locales remain unchanged.
 */
function buildLanguageHierarchy(languages: string[], defaultLanguage: string) {
    const available = new Set(languages);

    const fallback: Record<string, string[]> = {};

    for (const lang of languages) {
        const chain: string[] = [];
        const parts = lang.split("-");

        while (parts.length > 1) {
            parts.pop();

            const parent = parts.join("-");

            if (available.has(parent)) {
                chain.push(parent);
            }
        }

        // Every language ultimately falls back to the configured default language.
        if (lang !== defaultLanguage && available.has(defaultLanguage)) {
            chain.push(defaultLanguage);
        }

        fallback[lang] = chain;
    }

    fallback.default = [defaultLanguage];

    const resolveHierarchy = (code: string, fallbackCode?: string) => {
        return [
            ...(available.has(code) ? [code] : []),
            ...(fallback[code] ?? []),
            ...(fallbackCode ? [fallbackCode] : []),
        ].filter((value, index, array) => array.indexOf(value) === index);
    };

    return {
        fallback,
        resolveHierarchy,
    };
}

/**
 * Custom processor to show value + key
 */
const keyAnnotatorPostProcessor = {
    name: "keyAnnotator",
    type: "postProcessor" as const,
    process: (value: string, key: string | string[]) => {
        // Standardize key extraction (handles array or string keys)
        const keyName = Array.isArray(key) ? key[0] : key;

        // Skip annotation if value wasn't found or key is empty
        if (!value || !keyName) {
            return value;
        }

        // Format: "Account No (CASHLITE_ACCT_NO)"
        // Or customize to "CASHLITE_ACCT_NO: Account No" or "[CASHLITE_ACCT_NO] Account No"
        return `${value} (${keyName})`;
    },
};

/**
 * Initializes i18next once per page load and installs a global Zod error
 * map so any issue without an explicit errorKey still resolves to a
 * translation key instead of raw English — "components stay
 * localization-free" per the form-engine design doc.
 */
export async function ensureI18n() {
    if (initialized) {
        return i18next;
    }
    initialized = true;

    const resources: Record<string, { translation: Record<string, unknown> }> = {};

    const languages = AppConfig.getTranslationLanguages();
    const availableLanguages = AppConfig.getAvailableLanguages();
    const defaultLanguage = AppConfig.getDefaultLanguage() ?? "en";

    await Promise.all(
        languages.map(async (lang) => {
            resources[lang] = {
                translation: await AppConfig.loadLanguageResource(lang),
            };
        }),
    );

    const { fallback, resolveHierarchy } = buildLanguageHierarchy(languages, defaultLanguage);

    installConfigPostProcessor(i18next);

    await i18next
        .use(keyAnnotatorPostProcessor)
        .use(initReactI18next)
        .init({
            // Natively apply the post-processor globally IF debug mode is active
            // Disable the keyAnnotator since we are using <span /> to show the keys
            // postProcess: AppConfig.getIsCiModeAvailable() ? ["config", "keyAnnotator"] : [],
            postProcess: ["config"],
            resources,
            lng: defaultLanguage,
            fallbackLng: fallback,
            supportedLngs: availableLanguages,
            interpolation: {
                escapeValue: false,
            },
        });

    i18next.services.languageUtils.toResolveHierarchy = resolveHierarchy;

    z.setErrorMap((issue) => {
        if (issue.code === "too_small" || issue.code === "invalid_type") {
            return { message: "validation.required" };
        }

        return undefined;
    });

    return i18next;
}
